import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  type TravelersCheck,
  type TravelersCheckDetail,
  type TravelersCheckFormValues,
  TravelersCheckSchema,
  TravelersCheckDetailSchema,
} from "../models/travelersCheck"
import { travelersCheckApi } from "../services/travelersCheckApi"
import { useCrudList, useCrudItem } from "../../../hooks/useCrud"

const BASE_KEY = "travelers-checks"
const BASE_URL = "travelers-check"

export function useTravelersChecks(filters?: Record<string, unknown>) {
  return useCrudList<TravelersCheck>(BASE_KEY, BASE_URL, TravelersCheckSchema, filters)
}

export function useTravelersCheck(id?: number | null) {
  return useCrudItem<TravelersCheckDetail>(BASE_KEY, BASE_URL, TravelersCheckDetailSchema, id)
}

export function useCreateTravelersCheck() {
  const queryClient = useQueryClient()
  return useMutation<TravelersCheckDetail, Error, TravelersCheckFormValues>({
    mutationFn: (values) => travelersCheckApi.create(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useUpdateTravelersCheck() {
  const queryClient = useQueryClient()
  return useMutation<TravelersCheckDetail, Error, { id: number; values: TravelersCheckFormValues }>({
    mutationFn: ({ id, values }) => travelersCheckApi.update(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useDeleteTravelersCheck() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => travelersCheckApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useRequestRedeemForm() {
  const queryClient = useQueryClient()
  return useMutation<TravelersCheckDetail, Error, { travel_check_id: number; issuing_file: number; redeem_file: number }>({
    mutationFn: (payload) => travelersCheckApi.requestRedeemForm(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useRequestRedeem() {
  const queryClient = useQueryClient()
  return useMutation<TravelersCheckDetail, Error, { id: number; payload: { redeem_file: number; issuing_file?: number } }>({
    mutationFn: ({ id, payload }) => travelersCheckApi.requestRedeem(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useHandleRedeemRequest() {
  const queryClient = useQueryClient()
  return useMutation<TravelersCheckDetail, Error, { id: number; accept: boolean }>({
    mutationFn: ({ id, accept }) => travelersCheckApi.handleRedeemRequest(id, accept),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useRedeem() {
  const queryClient = useQueryClient()
  return useMutation<TravelersCheckDetail, Error, number>({
    mutationFn: (id) => travelersCheckApi.redeem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useCheckAgencies() {
  return useQuery({
    queryKey: [BASE_KEY, "agencies"],
    queryFn: () => travelersCheckApi.getAgencies(),
    staleTime: 1000 * 60 * 30,
  })
}
